<template>
  <div class="local-body">
    <CosmicBackground />

    <section class="responsive-center">
      <h1 class="title-cosmic mb-1">{{ $t("onboarding.welcome") }}</h1>
      <p class="subtitle-cosmic mb-6">{{ $t("onboarding.nicknamePrompt") }}</p>

      <v-theme-provider dark>
        <div class="meta-form pa-6">
          <v-text-field
            v-model="nickname"
            :label="$t('onboarding.nickname')"
            :placeholder="$t('onboarding.nickname')"
            maxlength="12"
            counter="12"
            outlined
            dense
            autofocus
            color="#c9b8ff"
            @keyup.enter="onNext"
          ></v-text-field>

          <!-- 국가 설정 (검색 가능) -->
          <v-autocomplete
            v-model="countryCode"
            :items="countryItems"
            :label="$t('nav.country')"
            :no-data-text="$t('nav.country')"
            outlined
            dense
            hide-details
            dark
            color="#c9b8ff"
            item-color="#c9b8ff"
            class="mt-1"
          >
            <template #item="{ item }">
              <span class="mr-2">{{ item.emoji }}</span
              >{{ item.text }}
            </template>
            <template #selection="{ item }">
              <span class="mr-2">{{ item.emoji }}</span
              >{{ item.text }}
            </template>
          </v-autocomplete>

          <!-- 언어 설정 (선택 즉시 화면 언어 반영) -->
          <v-select
            v-model="locale"
            :items="languageItems"
            :label="$t('settings.language')"
            outlined
            dense
            hide-details
            dark
            color="#c9b8ff"
            item-color="#c9b8ff"
            class="mt-4"
            @change="onChangeLanguage"
          ></v-select>

          <v-checkbox
            v-model="agreed"
            hide-details
            dense
            color="#c9b8ff"
            class="mt-1"
            :label="$t('onboarding.agreeTerms')"
          ></v-checkbox>
        </div>
      </v-theme-provider>

      <div class="d-flex justify-end mt-4">
        <v-btn
          class="cosmic-cta"
          min-width="130px"
          rounded
          :loading="isLoading"
          :disabled="!nickname.trim() || !agreed"
          @click="onNext"
        >
          <span>{{ $t("onboarding.next") }}</span>
        </v-btn>
      </div>
    </section>
  </div>
</template>

<script>
import SupabaseApi from "@/api/SupabaseApi";
import CosmicBackground from "../../components/CosmicBackground.vue";
import { setLocale } from "@/plugins/i18n";
import { listCountries } from "@/utils/countries";

// 로케일 → 기본 국가 추정 (초기값 편의용, 사용자가 변경 가능)
const LOCALE_TO_COUNTRY = { ko: "KR", ja: "JP", en: "US" };

export default {
  name: "UserSetupPage",
  components: { CosmicBackground },
  data() {
    return {
      nickname: "",
      agreed: false,
      isLoading: false,
      // 언어 초기값 = 브라우저 기본값(없으면 english). $i18n.locale가 이미 detectLocale(브라우저→en) 결과.
      locale: this.$i18n.locale,
      countryCode: null,
      languageItems: [
        { text: "한국어", value: "ko" },
        { text: "English", value: "en" },
        { text: "日本語", value: "ja" },
      ],
    };
  },
  computed: {
    // 현재 로케일로 현지화된 국가 목록 (언어 바꾸면 국가명도 함께 갱신)
    countryItems() {
      return listCountries(this.locale).map((c) => ({ text: c.name, value: c.code, emoji: c.emoji }));
    },
  },
  async created() {
    // 기존 이름/언어/국가(OAuth·계정) 프리필. 언어 미설정이면 브라우저 기본값 유지.
    try {
      const account = await SupabaseApi.UserAccount.whoAmI();
      this.nickname = (account && account.name) || this.$store.state.userName || "";
      if (account && account.locale) {
        this.locale = account.locale;
        setLocale(account.locale);
        this.$vuetify.lang.current = account.locale;
      }
      this.countryCode = (account && account.country_code) || LOCALE_TO_COUNTRY[this.locale] || null;
    } catch (e) {
      this.nickname = this.$store.state.userName || "";
      this.countryCode = LOCALE_TO_COUNTRY[this.locale] || null;
    }
  },
  methods: {
    // 언어 선택 즉시 화면 반영 (저장은 onNext에서)
    onChangeLanguage(lang) {
      setLocale(lang);
      this.$vuetify.lang.current = lang;
    },
    async onNext() {
      const name = this.nickname.trim();
      if (!name) return;
      try {
        this.isLoading = true;
        await SupabaseApi.UserAccount.updateNickname(name);
        this.$store.commit("__setUserName", name);
        // 언어·국가 저장 (브라우저 기본값이라도 명시 저장 → 이후 기기 간 유지)
        await SupabaseApi.UserAccount.updateLocale(this.locale);
        if (this.countryCode) {
          await SupabaseApi.UserAccount.updateCountryCode(this.countryCode);
          this.$store.commit("__setUserCountryCode", this.countryCode);
        }
        // 펫 생성(셋업 모드) STEP 1(펫 뽑기)로 — step 쿼리 포함
        this.$router.push("/pet/new?mode=setup&step=1");
      } catch (error) {
        console.error(error);
        alert(this.$t("onboarding.nicknameSaveError"));
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.local-body {
  position: relative;
  width: 100%;
  min-height: 100vh;
  overflow: auto;
  background-color: #0c0822;
  display: flex;
}

.responsive-center {
  position: relative;
  z-index: 1;
  width: 90%;
  max-width: 460px;
  margin: auto;
  padding: 40px 0;
}

.title-cosmic {
  text-align: center;
  font-family: "Ownglyph_meetme-Rg", "maplestory", sans-serif;
  font-weight: 400;
  font-size: clamp(30px, 5vw, 44px);
  color: #f3effc;
  text-shadow: 0 0 18px rgba(124, 92, 255, 0.45);
}
.subtitle-cosmic {
  text-align: center;
  color: #b9aee0;
  font-size: 18px;
}

/* 완전 다크 글래스 폼 패널 */
.meta-form {
  background: rgba(22, 16, 45, 0.62);
  border-radius: 16px;
  border: 1px solid rgba(149, 85, 253, 0.34);
  backdrop-filter: blur(10px);
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.5), 0 0 36px rgba(124, 92, 255, 0.22),
    0 0 0 1px rgba(255, 255, 255, 0.04) inset;
}
.meta-form >>> .v-input input {
  color: #f3effc;
}
.meta-form >>> .v-label {
  color: #b9aee0;
}
.meta-form >>> .theme--dark.v-text-field--outlined fieldset {
  border-color: rgba(201, 184, 255, 0.32);
}

/* 공용 CTA — 404/펫 생성 버튼과 동일 룩 */
.cosmic-cta {
  font-family: "Ownglyph_meetme-Rg", "maplestory", sans-serif !important;
  font-size: 21px !important;
  color: #fff !important;
  background: linear-gradient(135deg, #7c5cff 0%, #b15bff 45%, #ff6fae 100%) !important;
  box-shadow: 0 10px 28px rgba(124, 92, 255, 0.5) !important;
  letter-spacing: 0.5px;
}
</style>
